import React from 'react'

import { BreakdownControl } from 'features/charts-v2/components/BreakdownControl'
import type { BreakdownControlProps } from 'features/charts-v2/components/BreakdownControl'
import type {
  ControlsSidebarSection,
  ControlsSidebarBuiltInSection,
} from 'features/charts-v2/components/ControlsSidebar/ControlsSidebar'
import { FilterControl } from 'features/charts-v2/components/FilterControl'
import type { FilterControlProps } from 'features/charts-v2/components/FilterControl'
import { MetricControl } from 'features/charts-v2/components/MetricControl'
import type { MetricControlProps } from 'features/charts-v2/components/MetricControl'

// Named so the comparison below is against a constant rather than a raw
// string literal (bully: raw-string-literal-comparison).
const CHART_SECTION: ControlsSidebarBuiltInSection = 'chart'

export interface SidebarSectionsProps {
  sections: readonly ControlsSidebarSection[]
  metric?: MetricControlProps
  breakdown?: BreakdownControlProps
  filter?: FilterControlProps
  liftFilterHeaderActions: boolean
  filterHeaderSlot: HTMLDivElement | null
}

/**
 * Renders every non-'chart' section in the order the caller declared —
 * 'chart' renders separately, inside `ControlsSidebar`'s always-present
 * header wrapper. A custom section ({ id, content }) just renders its
 * content; a built-in id maps to its charts-v2 control component.
 */
export const SidebarSections: React.FC<SidebarSectionsProps> = ({
  sections,
  metric,
  breakdown,
  filter,
  liftFilterHeaderActions,
  filterHeaderSlot,
}) => (
  <>
    {sections
      .filter((section) => section !== CHART_SECTION)
      .map((section) => {
        if (typeof section !== 'string') {
          return (
            <React.Fragment key={section.id}>{section.content}</React.Fragment>
          )
        }
        if (section === 'metric') {
          return metric && <MetricControl key="metric" {...metric} />
        }
        if (section === 'breakdown') {
          return (
            breakdown && <BreakdownControl key="breakdown" {...breakdown} />
          )
        }
        if (section === 'filter') {
          return (
            filter && (
              <FilterControl
                key="filter"
                {...filter}
                headerActionsPortalTarget={
                  liftFilterHeaderActions ? filterHeaderSlot : undefined
                }
              />
            )
          )
        }
        return null
      })}
  </>
)
